import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Share2, Copy, CheckCircle2, Sparkles, CalendarDays, Users, MapPin, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { SHARE_DOMAIN } from '@/lib/shareDomain';

const authFetch = (url, opts = {}) => {
  const t = localStorage.getItem('session_token');
  const headers = { ...(opts.headers || {}), ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  return fetch(url, { ...opts, credentials: 'include', headers });
};

/**
 * Dots of Connection tab for Guide / Driver / Transport vendors.
 * Renders a fixed-height tour-history list (live-computed from tour records)
 * and a Share button that generates a public link celebrating their footprint.
 * Pure operator view — no public PII exposed.
 */
const VendorDotsOfConnection = ({ vendorId, vendorName, vendorType }) => {
  const navigate = useNavigate();
  const [history, setHistory] = useState(null);
  const [tokenState, setTokenState] = useState({ loading: true, token: null });

  const loadHistory = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/pollen/vendors/${vendorId}/tour-history`);
      if (r.ok) setHistory(await r.json()); else setHistory({ tours: [], total: 0 });
    } catch { setHistory({ tours: [], total: 0 }); }
  }, [vendorId]);

  useEffect(() => {
    loadHistory();
    // Check for existing token by posting (idempotent — server returns existing if one exists).
    (async () => {
      try {
        const r = await authFetch(`${API}/pollen/vendors/${vendorId}/share-token`, { method: 'POST' });
        if (r.ok) { const d = await r.json(); setTokenState({ loading: false, token: d.share_token }); }
        else setTokenState({ loading: false, token: null });
      } catch { setTokenState({ loading: false, token: null }); }
    })();
  }, [vendorId, loadHistory]);

  const v = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  // Pretty share URL on the operator's own pollendots.com domain.
  // ArtWorksIT's Nginx proxies pollendots.com/* through to the Emergent app,
  // where the /connection/crew/:token React route renders the public crew share.
  const shareUrl = tokenState.token ? `${SHARE_DOMAIN}/connection/crew/${tokenState.token}?v=${v}` : '';
  // Direct preview URL — only used by the Preview button so admins testing
  // the page see the real share layout (still hits the local origin so the
  // operator can preview offline before the proxy is live).
  const previewUrl = tokenState.token ? `${window.location.origin}/connection/crew/${tokenState.token}` : '';

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied');
    } catch { toast.error('Could not copy'); }
  };

  // One-tap WhatsApp send — opens WhatsApp web/app with the OG-friendly URL
  // pre-filled so the operator never has to copy/paste a URL manually
  // (which is how wrong URLs end up shared from address bars).
  const sendOnWhatsApp = () => {
    if (!shareUrl) return;
    const text = `${vendorName}'s dots of connection — every trail walked, every hidden spot discovered, every traveller carried.\n\n${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const rotateToken = async () => {
    if (!confirm('Rotating invalidates the previous link. Continue?')) return;
    await authFetch(`${API}/pollen/vendors/${vendorId}/share-token`, { method: 'DELETE' });
    const r = await authFetch(`${API}/pollen/vendors/${vendorId}/share-token`, { method: 'POST' });
    if (r.ok) { const d = await r.json(); setTokenState({ loading: false, token: d.share_token }); toast.success('New share link generated'); }
  };

  if (!history || tokenState.loading) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  const tours = history.tours || [];
  return (
    <div className="space-y-3">
      {/* Share link card */}
      <Card>
        <CardContent className="pt-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Sparkles className="w-4 h-4 text-amber-500" />
            Public share link — "{vendorName}'s dots of connection"
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            A shareable map celebrating every trail this {vendorType.toLowerCase()} has walked with Pollen Dots travellers.
            <strong className="text-foreground"> Always use the Copy button</strong> &mdash; that URL renders a thumbnail card on WhatsApp.
            The Preview button opens the page directly for testing only.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input readOnly value={shareUrl} onClick={e => e.target.select()} className="h-9 text-xs font-mono bg-muted/60 flex-1 min-w-0" data-testid="vendor-share-url" />
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={sendOnWhatsApp} data-testid="vendor-share-whatsapp">
              <MessageCircle className="w-3.5 h-3.5 mr-1" />Send on WhatsApp
            </Button>
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="vendor-share-copy"><Copy className="w-3.5 h-3.5 mr-1" />Copy</Button>
            <Button size="sm" variant="outline" asChild>
              <a href={previewUrl} target="_blank" rel="noopener noreferrer" data-testid="vendor-share-preview"><Share2 className="w-3.5 h-3.5 mr-1" />Preview</a>
            </Button>
            <Button size="sm" variant="ghost" onClick={rotateToken} data-testid="vendor-share-rotate">Rotate</Button>
          </div>
        </CardContent>
      </Card>

      {/* Aggregate counters */}
      <div className="grid grid-cols-3 gap-2">
        <Card><CardContent className="py-3 text-center">
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1 justify-center"><CalendarDays className="w-3 h-3" />Tours served</div>
          <div className="text-xl font-semibold mt-0.5">{tours.length}</div>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1 justify-center"><Users className="w-3 h-3" />Travellers</div>
          <div className="text-xl font-semibold mt-0.5">
            {tours.reduce((s, t) => s + (parseInt(t.adults || 0) + parseInt(t.children || 0) || parseInt(t.num_travellers || 0) || 0), 0)}
          </div>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1 justify-center"><MapPin className="w-3 h-3" />Destinations</div>
          <div className="text-xl font-semibold mt-0.5">
            {new Set(tours.map(t => (t.destination || '').split(',')[0].trim()).filter(Boolean)).size}
          </div>
        </CardContent></Card>
      </div>

      {/* Fixed-height scrollable tour list */}
      <Card>
        <CardContent className="pt-3 pb-2">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">Tour history</div>
            <Badge variant="outline" className="text-[10px]">Auto from tours &mdash; no manual entry</Badge>
          </div>
          {tours.length === 0 ? (
            <p className="text-xs text-muted-foreground py-6 text-center">
              No tours yet. This list populates automatically when {vendorName} is assigned on a tour's guides or transport.
            </p>
          ) : (
            <div className="max-h-72 overflow-y-auto pr-1 divide-y" data-testid="vendor-tour-history">
              {tours.map(t => (
                <button
                  key={t.id}
                  onClick={() => navigate(`/pollen-dots/tour/${t.id}`)}
                  className="w-full text-left px-2 py-2 hover:bg-muted/40 rounded flex items-center gap-2 transition"
                  data-testid={`vendor-tour-${t.id}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{t.destination || t.tour_name || 'Tour'}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {(t.customer_name || '—')} &middot; {t.start_date || '—'}
                      {t.end_date && t.end_date !== t.start_date ? ` → ${t.end_date}` : ''}
                      {(t.adults || t.children || t.num_travellers) && ` · ${(parseInt(t.adults || 0) + parseInt(t.children || 0)) || t.num_travellers} pax`}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorDotsOfConnection;
